import React, { useEffect, useMemo, useState } from 'react';
import { JsonForms } from '@jsonforms/react';
import { combineAntView } from '@components/uploadForm';
import { Spinner } from '@dynamika-shared/ui/Spinner/Spinner';
import { NextButton } from '@components/SiteUserUpload/Components/NextButton';
import { useParams } from 'react-router-dom';
import { LoadingFile } from '@components/SiteUserUpload/Components/Steps/components/LoadingFile';
import { useSiteUploadSlice } from '@restore/siteUpload/siteUpload.slice';
import { FormState } from '@restore/siteUpload/siteUpload.types';
import { Divider, Radio } from 'antd';
import { UploadFormData } from '@components/SiteUserUpload/types/uploadTypes';
import { NoEDTF } from './NoEDTF';
import { isEmpty } from 'lodash';
import { JsonSchema } from '@jsonforms/core';
import Ajv from 'ajv';
import ajvErrors from 'ajv-errors';

type UploadFormEdtfProp = {
  setCurrentStep: (step: number) => void;
  currentStep: number;
};
const ajv = ajvErrors(
  new Ajv({ allErrors: true, useDefaults: true, strict: false })
);

const populateMissingBooleans = (
  schema: JsonSchema,
  data: FormState
): FormState => {
  const result: FormState = data ? { ...data } : {};

  if (!schema?.properties) return result;
  for (const [key, prop] of Object.entries(schema.properties)) {
    if (
      typeof prop === 'object' &&
      prop?.type === 'boolean' &&
      result[key] === undefined
    ) {
      result[key] = false;
    }
  }

  return result;
};

const getSchemaName = (schema: UploadFormData) =>
  schema?.modalityNames?.join?.(' and ') ?? 'Unknown form';

export const UploadFormEdtf: React.FC<UploadFormEdtfProp> = ({
  setCurrentStep,
  currentStep,
}) => {
  const [
    schemas,
    updateEDTFSchemas,
    edtfData,
    updateEDTFData,
    error,
    clearError,
    selectedSchema,
    selectEDTFSchema,
    schemasLoading,
  ] = useSiteUploadSlice((state, actions) => [
    state.schemas,
    actions.updateEDTFSchemas,
    state.edtfData,
    actions.updateEdtfData,
    state.error,
    actions.clearError,
    state.selectedSchema,
    actions.selectEDTFSchema,
    state.schemasLoading,
  ]);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isFormTouched, setIsFormTouched] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const { id: clinicalTrialId, siteId } = useParams<{
    id: string;
    siteId: string;
  }>();

  const validateForm = (edtfData: FormState) => {
    if (!selectedSchema?.schema) {
      setIsFormValid(false);
      return;
    }
    const isValid = ajv.validate(selectedSchema.schema, edtfData);
    setIsFormValid(isValid);
  };

  const handleFormChange = ({ data }: { data: FormState }) => {
    updateEDTFData(data);
    if (!isInitialized) {
      setIsInitialized(true);
      return;
    }

    if (isFormTouched) {
      validateForm(data);
    } else {
      setIsFormTouched(true);
      validateForm(data);
    }
  };

  useEffect(() => {
    if (schemas?.length >= 1 && !selectedSchema) {
      const firstSchema = schemas[0];
      const initialData = populateMissingBooleans(firstSchema.schema, {});
      updateEDTFData(initialData);
      selectEDTFSchema(firstSchema);
      setIsInitialized(false);
    }
  }, [schemas, selectedSchema]);

  const jsonFormAntView = useMemo(() => combineAntView(), [clinicalTrialId]);

  const handleSubmitForm = async () => {
    if (clinicalTrialId && siteId) {
      updateEDTFSchemas(edtfData as FormState);
      setCurrentStep(currentStep + 1);
    }
  };

  const handleTryAgain = () => {
    setCurrentStep(0);
    clearError();
  };
  if (error) {
    return (
      <LoadingFile
        text={'File Processing Failed'}
        subTitle={
          'Processing failed. The uploaded data was not saved.\nPlease try again later or contact support for assistance.'
        }
        spinnerType={'Semicircle'}
        tryAgainButton
        tryAgainFunc={handleTryAgain}
      />
    );
  }

  if (schemasLoading) {
    return (
      <div className='flex h-full w-full items-center justify-center'>
        <Spinner spinCircleColor='#F26E23' spinColor='#FFF6F0' />
      </div>
    );
  }
  if (
    !schemasLoading &&
    ((schemas && schemas.every(schema => isEmpty(schema.schema))) ||
      isEmpty(selectedSchema?.schema) ||
      isEmpty(selectedSchema?.uiSchema))
  ) {
    return (
      <div className='w-full h-full'>
        <NoEDTF />
        <div className='flex justify-end'>
          <NextButton onClick={() => setCurrentStep(currentStep + 1)} />
        </div>
      </div>
    );
  }
  return (
    <div data-testid='UploadFormEdtf' className='w-full h-full'>
      <div>
        <div>Select Form</div>
        <Radio.Group
          className='flex justify-start items-center my-2'
          value={selectedSchema?.id}
          name='EDTFselection'
        >
          {schemas?.map((schema, index) => {
            return (
              <div key={schema.id}>
                <Radio
                  value={schema.id}
                  className='text-left'
                  onClick={() => {
                    updateEDTFData(null);
                    setIsFormValid(false);
                    selectEDTFSchema(schema);
                  }}
                >
                  {getSchemaName(schema)}
                </Radio>
                {index < schemas.length - 1 && (
                  <Divider type={'vertical'} className='pr-1' />
                )}
              </div>
            );
          })}
        </Radio.Group>
      </div>
      {selectedSchema && (
        <>
          <div className='mb-[22px] flex items-center justify-between font-medium rounded-[8px] bg-[#F6F9FF] p-[12px] text-1824 border-[1px] border-brightGray'>
            eDTF - {getSchemaName(selectedSchema)}
          </div>
          <JsonForms
            ajv={ajv}
            key={selectedSchema?.id}
            schema={selectedSchema.schema}
            uischema={selectedSchema.uiSchema}
            data={edtfData}
            renderers={jsonFormAntView.renderers}
            cells={jsonFormAntView.cells}
            onChange={handleFormChange}
            validationMode={isFormTouched ? 'ValidateAndShow' : 'NoValidation'}
          />
          <div className='flex justify-end pt-2'>
            <NextButton
              onClick={isFormValid ? handleSubmitForm : undefined}
              disabled={!isFormValid}
            />
          </div>
        </>
      )}
    </div>
  );
};
